import { StepWizardContextProps, StepWizardProps } from './StepWizard.types';
import logo from '../../../assets/images/logo_side_cropped.png';
import { ReactComponent as CheckIcon } from '../../../assets/vectors/check.svg';
import { ReactComponent as UncheckIcon } from '../../../assets/vectors/uncheck.svg';
import { useEffect, useMemo, useState } from 'react';
import { StepWizardContext } from './StepWizard.context';
import { useCurrentUser } from '../../../hooks/useCurrentUser/useCurrentUser';
import { useLocation } from 'react-router-dom';
import '../../../styles/StepWizard.css';
import Empty from '../Empty/Empty';

function separatePrimaryColorTags(input: string): string[] {
  const regex = /<primary>(.*?)<\/primary>/g;
  const result: string[] = [];
  const colorTags: string[] = [];
  let match;

  while ((match = regex.exec(input)) !== null) {
    colorTags.push(match[1]);
  }

  if (colorTags.length === 1) {
    const parts = input.split(/<\/?primary>/);
    result.push(parts[0], colorTags[0], parts[2]);
  } else {
    result.push(input);
  }

  return result.filter(Boolean);
}

function separateSecondaryColorTags(input: string): string[] {
  const regex = /<secondary>(.*?)<\/secondary>/g;
  const result: string[] = [];
  const colorTags: string[] = [];
  let match;

  while ((match = regex.exec(input)) !== null) {
    colorTags.push(match[1]);
  }

  if (colorTags.length === 1) {
    const parts = input.split(/<\/?secondary>/);
    result.push(parts[0], colorTags[0], parts[2]);
  } else {
    result.push(input);
  }

  return result.filter(Boolean);
}

export default ({
  steps,
  defaultStep,
  isCompany,
  logoLink,
  sidebarHidden,
  isFullscreen = true
}: StepWizardProps) => {
  const location = useLocation();

  const currentUser = useCurrentUser();

  const [currentStep, setCurrentStep] = useState(defaultStep || 1);

  const contextValue = useMemo<StepWizardContextProps>(
    () => ({ currentStep, setCurrentStep }),
    [currentStep, setCurrentStep]
  );

  useEffect(() => {
    if (defaultStep) {
      setCurrentStep(defaultStep);
    }
  }, [defaultStep]);

  useEffect(() => {
    const [, namespace] = location.pathname.split('/');
    if (currentUser && namespace === 'step_wizard') {
      setCurrentStep(currentUser.id_step + 1);
    }
  }, [currentUser, location]);

  const parseTitle = (title: string) => {
    const isSecondaryColor =
      separateSecondaryColorTags(title).length > separatePrimaryColorTags(title).length;

    const [start, colored, end] = isSecondaryColor
      ? separateSecondaryColorTags(title)
      : separatePrimaryColorTags(title);

    return {
      simple: (
        <>
          {start}
          {colored}
          {end}
        </>
      ),
      colored: (
        <>
          {start}
          <span className={`text-${isSecondaryColor ? 'secondary' : 'primary'}`}>{colored}</span>
          {end}
        </>
      ),
    };
  };

  if (!steps) return <></>;

  const {
    component: CurrentStep,
    title,
    subtitle,
  } = steps.find((step) => step.step === currentStep) || {
    component: () => <Empty>{''}</Empty>,
    title: '',
  };

  return (
    <StepWizardContext.Provider value={contextValue}>
      <div className={isFullscreen ? 'fullscreen' : 'flex-contents'}>
        <div className="step">
          {!sidebarHidden &&
            <div className="step__sidebar">
              <div className="flex-column">
                <a href={logoLink || '/'}>
                  <img className="step__logo" src={logo} />
                </a>
                <div className="step__steps-list">
                  {steps.map(
                    ({ step, title, subtitle, hidden, backDisabled }, stepIndex) =>
                      !hidden && (
                        <div
                          key={stepIndex}
                          className={`step__steps-list-item${step === currentStep ? ' step__steps-list-item--active' : ''
                            }${step < currentStep && !backDisabled ? ' step__steps-list-item--done' : ''
                            }${step < currentStep && backDisabled
                              ? ' step__steps-list-item--done step__steps-list-item--done-back-disabled'
                              : ''
                            }`}
                          {...(step < currentStep &&
                            !backDisabled && {
                            onClick: () => setCurrentStep(step),
                          })}>
                          {step >= currentStep ? (
                            <UncheckIcon
                              style={{ fill: isCompany ? 'var(--secondary)' : 'var(--primary)' }}
                            />
                          ) : (
                            <CheckIcon />
                          )}
                          <div>
                            <label>{parseTitle(title).colored}</label>
                            <span>{subtitle}</span>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>
              <div className="step__sidebar-footer">
                <a href="https://www.lens-group.fr/" target="_blank" rel="noreferrer">
                  <span>© LENS GROUP</span>
                </a>
                <a href="mailto:contact@publiconsult.fr">
                  <span>contact@publiconsult.fr</span>
                </a>
              </div>
            </div>
          }
          <main className="step__main">
            {
              isFullscreen
                ?
                <>
                  <h1 className="step__title">{parseTitle(title).colored}</h1>
                  <h4 className="step__subtitle">{subtitle}</h4>
                  <CurrentStep />
                  <div className="step__indicator">
                    {steps.map(({ step }, stepIndex) => (
                      <div
                        key={stepIndex}
                        className={`step__indicator-dot${step === currentStep
                          ? isCompany
                            ? ' step__indicator-dot-secondary--active'
                            : ' step__indicator-dot--active'
                          : ''
                          }`}
                      />
                    ))}
                  </div>
                </>
                :
                <CurrentStep />
            }
          </main>
        </div>
      </div>
    </StepWizardContext.Provider>
  );
};
